import {
  Globe, Smartphone, Palette, CalendarCheck, AtSign, Megaphone, Bot, Rocket,
  TrendingUp, Workflow, Camera, PenTool, Target, Search, Mail, Users,
  BrainCircuit, Plug, BarChart3,
} from "lucide-react";

export const PILLARS = [
  {
    id: "launch",
    name: "Launch",
    icon: Rocket,
    tagline: "Everything a business needs to get started.",
    services: [
      {
        icon: Palette,
        title: "Branding & Logo",
        short: "A logo and look that makes your business instantly recognisable.",
        detail: "People judge a business in seconds. We craft logos, colour palettes, and fonts that feel professional and unmistakably you — delivered as a simple brand guide anyone on your team can follow.",
        deliverables: ["Logo in all formats you need", "Colour & font guide", "Social media profile kit", "Business card & email signature designs"],
      },
      {
        icon: Globe,
        title: "Website",
        short: "A fast, mobile-friendly website that turns visitors into customers.",
        detail: "Your website is your hardest-working employee — open 24/7, never takes a sick day. We design and build clean, quick-loading sites that explain what you do in seconds and make it easy for people to contact you, book, or buy.",
        deliverables: ["Custom design, no cookie-cutter templates", "Mobile-first and fast loading", "Contact forms & Google Maps", "Basic SEO so locals can find you"],
      },
      {
        icon: Smartphone,
        title: "Mobile App",
        short: "Custom apps and tools that fit how your business actually works.",
        detail: "Need something beyond a website — a customer portal, a quoting tool, an app your clients can download? We build practical web and mobile apps that solve one problem really well, without enterprise price tags.",
        deliverables: ["Web apps & progressive mobile apps", "Customer portals and dashboards", "Quote calculators & custom tools", "Built to grow with you"],
      },
      {
        icon: AtSign,
        title: "Domain & Business Email",
        short: "yourbusiness.com.au and an email address that looks the part.",
        detail: "A @gmail.com address quietly tells customers you're small-time. We register your domain, set up professional email (you@yourbusiness.com.au), and connect it all to your devices — done for you, start to finish.",
        deliverables: ["Domain registration & renewal setup", "Professional email on all devices", "Spam protection & signatures", "We handle the technical bits"],
      },
      {
        icon: Bot,
        title: "AI Chatbot",
        short: "Instant answers for your customers, 24 hours a day.",
        detail: "Most customer questions are the same ten questions. An AI chatbot on your site answers them instantly — opening hours, pricing, bookings — and hands over to you only when it really needs a human.",
        deliverables: ["Trained on your business info", "Answers FAQs instantly", "Captures leads while you're busy", "Easy handover to you"],
      },
      {
        icon: CalendarCheck,
        title: "Booking & Payment Systems",
        short: "Let customers book and pay you any time — even while you sleep.",
        detail: "Stop losing bookings to phone tag and chasing bank transfers. We set up online booking with automatic reminders, plus trusted payment gateways like Stripe, so your calendar fills itself and money lands in your account.",
        deliverables: ["Booking calendar on your site", "Automatic confirmations & reminders", "Deposits to reduce no-shows", "Secure card & wallet payments"],
      },
    ],
  },
  {
    id: "grow",
    name: "Grow",
    icon: TrendingUp,
    tagline: "Help your business attract more customers.",
    services: [
      {
        icon: Camera,
        title: "Photography & Videography",
        short: "Scroll-stopping photos and video that show your business at its best.",
        detail: "Stock photos only go so far — customers want to see the real you. With a background in professional videography and photography, we shoot your space, your team, and your work so your website and socials look genuinely world-class.",
        deliverables: ["On-location photo & video shoots", "Edited image library you own", "Short-form videos for socials", "Website-ready hero shots"],
      },
      {
        icon: Megaphone,
        title: "Social Media Management",
        short: "Your socials handled — consistent, on-brand, zero late nights.",
        detail: "We plan, schedule, and manage your Instagram and Facebook so your business stays visible even when you're flat out running it. You approve the direction; we do the doing.",
        deliverables: ["Monthly content calendar", "Posting & scheduling done for you", "Comment & message guidance", "Simple monthly report"],
      },
      {
        icon: PenTool,
        title: "Content Creation",
        short: "Posts, blogs and graphics that sound like you — AI-drafted, human-polished.",
        detail: "We combine AI content tools with human editing to produce a steady stream of captions, articles, and graphics in your tone of voice — so you never stare at a blank caption box at 9pm again.",
        deliverables: ["Social posts & captions", "SEO-friendly blog articles", "On-brand graphics", "Your tone of voice, every time"],
      },
      {
        icon: Target,
        title: "Google & Meta Ads",
        short: "Ads that bring customers to you — without burning budget.",
        detail: "Done right, ads are a tap you can turn on when you need more work. We set up and manage Google and Meta campaigns with tight targeting and honest reporting, so every dollar is accounted for.",
        deliverables: ["Campaign setup & structure", "Audience & keyword targeting", "Ad creative that converts", "Monthly optimisation & reporting"],
      },
      {
        icon: Search,
        title: "SEO",
        short: "Get found on Google when locals search for what you do.",
        detail: "Most customers find local businesses through search. We optimise your website and Google Business Profile so you show up when people nearby search for your services — no tricks, just solid fundamentals.",
        deliverables: ["Keyword research for your area", "On-page optimisation", "Google Business Profile setup", "Monthly ranking updates"],
      },
      {
        icon: Mail,
        title: "Email Marketing",
        short: "Turn one-time buyers into regulars with emails people actually open.",
        detail: "Your past customers are your cheapest source of new revenue. We set up newsletters and automated emails that bring people back — welcome series, promotions, and gentle reminders that you exist.",
        deliverables: ["Newsletter setup & design", "Automated welcome series", "Promotional campaigns", "List growth made simple"],
      },
    ],
  },
  {
    id: "automate",
    name: "Automate",
    icon: Workflow,
    tagline: "Save time with AI and automation.",
    services: [
      {
        icon: Users,
        title: "CRM Setup",
        short: "Every customer, conversation and follow-up in one place.",
        detail: "Sticky notes and memory are not a system. We set up a simple CRM tailored to your business so every enquiry is tracked, every follow-up happens, and no customer ever falls through the cracks again.",
        deliverables: ["CRM configured for your business", "Existing contacts imported", "Enquiry & sales pipeline", "Plain-English training session"],
      },
      {
        icon: BrainCircuit,
        title: "AI Assistants",
        short: "A tireless helper that drafts replies, quotes and follow-ups for you.",
        detail: "Beyond the chatbot on your website, we build AI assistants that work inside your business — drafting email replies, preparing quotes, and nudging you about follow-ups, all in your tone of voice.",
        deliverables: ["Trained on your business info", "Email & quote drafting", "Follow-up reminders", "Works with the tools you use"],
      },
      {
        icon: Workflow,
        title: "Workflow Automation",
        short: "The repetitive admin, done automatically in the background.",
        detail: "Enquiry comes in, quote goes out, invoice follows, reminder sends — all without you touching a thing. We map your repetitive tasks and automate them with reliable, no-drama tools.",
        deliverables: ["Enquiry-to-invoice workflows", "Automatic reminders & follow-ups", "Form-to-CRM data entry", "Built on proven no-code tools"],
      },
      {
        icon: Plug,
        title: "Integrations",
        short: "Make your tools talk to each other so nothing gets typed twice.",
        detail: "Your website, booking system, payment gateway, accounting software and CRM should share information automatically. We connect them so data entered once appears everywhere it needs to be.",
        deliverables: ["Website, booking & payment links", "Accounting & CRM connections", "Automatic data syncing", "Fewer errors, less double-handling"],
      },
      {
        icon: BarChart3,
        title: "Dashboards & Reporting",
        short: "See how your business is doing at a glance — not in a spreadsheet.",
        detail: "Bookings, sales, ad results and website traffic, pulled into one clean dashboard you can check from your phone. Know what's working in ten seconds, and make decisions on facts instead of gut feel.",
        deliverables: ["Sales & bookings dashboard", "Marketing results in one view", "Automated weekly summaries", "Check it from your phone"],
      },
    ],
  },
];

export const SERVICES = PILLARS.flatMap((p) => p.services);

export const PRICING = [
  {
    name: "Starter",
    price: 799,
    tagline: "Get online properly, fast.",
    turnaround: "Live in about 1 week",
    features: [
      "3-page custom website",
      "Mobile-friendly & fast",
      "Contact form & Google Maps",
      "Domain & business email setup",
      "Basic SEO setup",
      "1 revision round",
    ],
    cta: "Start with Starter",
    featured: false,
  },
  {
    name: "Growth",
    price: 1999,
    tagline: "Look established. Win more work.",
    turnaround: "Live in about 2 weeks",
    features: [
      "Up to 8-page website",
      "Logo & brand mini-kit",
      "Online booking system OR payments",
      "Social media setup + 2 weeks of posts",
      "Google Business Profile setup",
      "2 revision rounds",
    ],
    cta: "Choose Growth",
    featured: true,
  },
  {
    name: "Complete",
    price: 3499,
    tagline: "The full digital engine room.",
    turnaround: "Live in about 3 weeks",
    features: [
      "Everything in Growth",
      "AI chatbot trained on your business",
      "Payment gateway integration",
      "4 weeks of social content",
      "App-style features as needed",
      "30 days priority support",
    ],
    cta: "Go Complete",
    featured: false,
  },
];

export const GROWTH_PACKAGES = [
  {
    name: "Essential Growth",
    price: "$690",
    tagline: "Steady, professional content every month.",
    features: ["2-hour monthly content shoot", "10 edited photos", "4 reels", "8 social media posts", "Content scheduling", "Monthly performance report"],
    featured: false,
    cta: "Start Essential",
  },
  {
    name: "Growth Plus",
    price: "$1,290",
    tagline: "Your full content engine, handled.",
    features: ["5-hour content shoot", "25 edited photos", "10 reels", "12 social posts + stories", "Caption writing", "Social media management", "Google Business updates", "Monthly strategy meeting"],
    featured: true,
    cta: "Choose Growth Plus",
  },
  {
    name: "Premium Growth",
    price: "$2,490",
    tagline: "Maximum reach, minimum effort.",
    features: ["8-hour content shoot", "Unlimited edited photos", "20 reels", "20 social media posts", "Drone footage", "Staff & customer interviews", "TikTok & YouTube Shorts", "Website updates", "AI chatbot updates", "Monthly analytics & strategy"],
    featured: false,
    cta: "Go Premium",
  },
];

export const AUTOMATE_PACKAGES = [
  {
    name: "AI Starter",
    price: "$290",
    tagline: "Dip a toe into automation.",
    features: ["AI chatbot monitoring", "Basic CRM", "Monthly updates", "Email support"],
    featured: false,
    cta: "Start with AI",
  },
  {
    name: "Business Automation",
    price: "$590",
    tagline: "Your admin, on autopilot.",
    features: ["AI chatbot", "CRM management", "Lead automation", "Appointment automation", "Workflow optimisation", "Monthly reporting"],
    featured: true,
    cta: "Automate my business",
  },
  {
    name: "Enterprise Automation",
    price: "From $990",
    tagline: "Custom-built around your business.",
    features: ["Custom AI solutions", "Multi-step automations", "CRM integrations", "API connections", "Dashboards", "Priority support"],
    featured: false,
    cta: "Talk to us",
  },
];

export const CARE_PLANS = [
  {
    name: "Essential",
    price: "$99",
    tagline: "Keep your site safe and current.",
    features: ["Hosting (if provided by you)", "Security monitoring", "Software updates", "Monthly backup"],
    featured: false,
    cta: "Protect my site",
  },
  {
    name: "Professional",
    price: "$199",
    tagline: "Safe, fast, and always improving.",
    features: ["Everything in Essential", "Up to 1 hour of content changes", "Performance optimisation", "Monthly health report"],
    featured: true,
    cta: "Choose Professional",
  },
  {
    name: "Premium",
    price: "$399",
    tagline: "Your website, fully managed.",
    features: ["Everything in Professional", "Up to 3 hours of website updates", "Landing pages", "Priority support"],
    featured: false,
    cta: "Go Premium",
  },
];

export const PLAN_OPTIONS = [
  ...PRICING.map((p) => `${p.name} Package`),
  ...GROWTH_PACKAGES.map((p) => `${p.name} (monthly)`),
  ...AUTOMATE_PACKAGES.map((p) => `${p.name} (monthly)`),
  ...CARE_PLANS.map((p) => `Care Plan ${p.name} (monthly)`),
];

export const ADDONS = [
  { name: "Extra Website Page", price: "$150/page" },
  { name: "Logo Design Only", price: "$690" },
  { name: "Business Cards", price: "$150" },
  { name: "Brand Style Guide", price: "$490" },
  { name: "Drone Photography", price: "From $350" },
  { name: "Drone Video", price: "From $450" },
  { name: "Extra Reels", price: "$120 each" },
  { name: "Extra Edited Photos", price: "$20 each" },
  { name: "Google Ads Management", price: "From $500/mo + ad spend" },
  { name: "Meta Ads Management", price: "From $500/mo + ad spend" },
  { name: "TikTok Ads Management", price: "From $500/mo + ad spend" },
  { name: "SEO Optimisation", price: "From $790" },
  { name: "Copywriting", price: "From $300" },
  { name: "Additional Filming", price: "$250/hour" },
];

export const TESTIMONIALS = [
  {
    quote: "Avora gave us a website that finally feels like our films — elegant, considered, effortless. Couples can explore our work and book a consultation in seconds.",
    name: "Emporium Weddings",
    role: "Luxury Wedding Photography, Sydney",
  },
  {
    quote: "As a brand-new business, trust was everything. Avora built us a home online that feels warm and established from day one.",
    name: "Family Legacy Films",
    role: "Legacy Documentaries, Sydney & Canberra",
  },
  {
    quote: "Avora took us from an idea to a live marketplace — brand, platform, emails, everything. One team, one invoice, zero headaches.",
    name: "Souq Australia",
    role: "Online Classifieds Marketplace",
  },
];

export const CASE_STUDIES = [
  {
    slug: "emporium-weddings",
    name: "Emporium Weddings",
    url: "https://www.emporiumweddings.com.au",
    domain: "emporiumweddings.com.au",
    industry: "Wedding Photography & Videography",
    tagline: "A luxury wedding studio with 13 years and 1,300+ weddings behind it — finally with an online presence to match.",
    services: ["Website", "Business Email", "Logo & Brand", "SEO", "CRM", "Booking Form"],
    images: ["/assets/cases/emporium-hero.jpg", "/assets/cases/emporium-mid.jpg", "/assets/cases/emporium-cta.jpg"],
    quote: {
      text: "Avora gave us a website that finally feels like our films — elegant, considered, effortless. Couples can explore our work and book a consultation in seconds, and every enquiry lands in one organised place.",
      author: "The Emporium Weddings team",
      role: "Sydney",
    },
    overview: "Emporium Weddings is a boutique Sydney studio specialising in luxury wedding photography and cinematic films, with over 13 years of experience and more than 1,300 multicultural weddings documented.",
    challenge: "Their work was premium, but their digital presence wasn't. Couples researching photographers judge in seconds — and every enquiry was being handled manually, scattered across inboxes, with no single system tracking a couple from first hello to booked date.",
    solution: [
      "A refined multi-page website with dedicated galleries for weddings, engagements, portraits and couples",
      "New logo and brand identity that feels as considered as their films",
      "Professional business email on their own domain",
      "SEO so Sydney couples actually find them",
      "A CRM that tracks every enquiry through to a booked wedding",
      "An online booking form that turns website visitors into consultations",
    ],
    outcome: "A website that finally matches the calibre of their photography — with every enquiry flowing into one organised system instead of a crowded inbox.",
  },
  {
    slug: "family-legacy-films",
    name: "Family Legacy Films",
    url: "https://www.familylegacyfilms.com.au",
    domain: "familylegacyfilms.com.au",
    industry: "Legacy Documentaries & Family Films",
    tagline: "A brand-new kind of business — cinematic films that preserve a family's story — launched with instant credibility.",
    services: ["Website", "Business Email", "Logo & Branding", "SEO", "Booking Form", "Social Media"],
    images: ["/assets/cases/familylegacy-hero.jpg", "/assets/cases/familylegacy-mid.jpg"],
    quote: {
      text: "As a brand-new business, trust was everything. Avora built us a home online that feels warm and established — and families can book a free chat the moment a story moves them.",
      author: "The Family Legacy Films team",
      role: "Sydney & Canberra",
    },
    overview: "Family Legacy Films creates cinematic legacy documentaries and family history films across Sydney and Canberra — preserving the voices, wisdom and warmth of loved ones — alongside dignified funeral livestreaming with same-day video.",
    challenge: "Launching a deeply personal service means families are trusting you with irreplaceable memories. The website had to build trust within seconds, explain an unfamiliar service clearly, and make the first step — a free chat — effortless.",
    solution: [
      "A warm, cinematic website covering legacy films, funeral services, packages, gallery and gift vouchers",
      "Logo and brand identity that balances warmth with professionalism",
      "Professional business email on their own domain",
      "SEO foundations so families searching for these services find them first",
      "A 'Book a Free Chat' booking flow front and centre on every page",
      "Social media setup to start telling stories where families already scroll",
    ],
    outcome: "A trust-building online home for a brand-new business — with free-chat bookings woven through every page and room to grow.",
  },
  {
    slug: "souq-australia",
    name: "Souq Australia",
    url: "https://www.souq.com.au",
    domain: "souq.com.au",
    industry: "Online Classifieds Marketplace",
    tagline: "A two-sided marketplace built from zero — brand, platform and marketing engine included.",
    services: ["Website", "Business Email", "Logo & Branding", "SEO", "Email Marketing"],
    images: null,
    quote: {
      text: "Avora took us from an idea to a live marketplace — brand, platform, emails, everything. One team, one invoice, zero headaches.",
      author: "The Souq Australia team",
      role: "Australia-wide",
    },
    overview: "Souq Australia is an online classifieds marketplace where anyone can create an account, submit ads and start earning — a platform play, not just a brochure site.",
    challenge: "Marketplaces live and die on trust. Souq needed to look established on day one — credible enough for people to create accounts, post listings and come back — while keeping the path from landing to posting an ad as short as possible.",
    solution: [
      "A clean, fast platform website with account creation and ad submission built in",
      "A bold logo and brand identity designed to feel familiar from first visit",
      "Professional business email for user and partner communication",
      "SEO foundations to grow organic listings traffic",
      "Email marketing to bring posters and browsers back to the platform",
    ],
    outcome: "A live, growing marketplace with the brand polish of an established player — and the email engine to keep users coming back.",
  },
];

export const PROJECTS = [
  { name: "Luna Bakehouse", industry: "Bakery & Café", work: ["Website", "Online Payments"], result: "Online pre-orders within 2 weeks of launch", before: "Instagram-only, orders via DM chaos", after: "Clean menu site with checkout" },
  { name: "Coastal Cuts Barber", industry: "Barbershop", work: ["Website", "Booking System"], result: "80% of bookings now happen online", before: "Phone bookings & walk-ins only", after: "24/7 online booking with reminders" },
  { name: "Harbour Physio", industry: "Physiotherapy", work: ["Website", "AI Chatbot"], result: "Front-desk phone calls cut in half", before: "Outdated site, no online info", after: "Modern site with instant FAQ bot" },
  { name: "Bloom & Co Florist", industry: "Florist", work: ["Branding", "Website"], result: "A brand as fresh as the flowers", before: "No logo, no site, word of mouth", after: "Full identity + 5-page site" },
  { name: "FixIt Plumbing", industry: "Trade Services", work: ["Website", "Domain & Email"], result: "Quote requests up 3x in a month", before: "Gmail address & a Facebook page", after: "Pro site, domain & branded email" },
  { name: "Sunny Side Café", industry: "Hospitality", work: ["Social Media", "AI Content"], result: "Consistent posting, zero late nights", before: "Posting once a month, if that", after: "12 scheduled posts every month" },
];

export const POSTS = [
  {
    slug: "website-refresh-signs",
    title: "5 Signs Your Small Business Needs a Website Refresh",
    excerpt: "If your website is older than your youngest staff member, it might be costing you customers. Here's how to tell.",
    date: "July 2026",
    readTime: "4 min read",
    tag: "Websites",
    body: [
      "Your website is often the first impression a customer gets of your business. And just like a shopfront with peeling paint, a tired website quietly turns people away before you ever get the chance to win them over.",
      "Sign one: it doesn't work properly on a phone. More than half of your visitors are browsing on mobile. If they have to pinch and zoom to read your menu or find your phone number, they'll simply leave.",
      "Sign two: it loads slowly. People give a website about three seconds before they hit the back button. Old sites with heavy images and outdated code are the usual culprits.",
      "Sign three: the information is out of date. Old prices, old hours, services you no longer offer — nothing erodes trust faster than a customer showing up to find your details were wrong.",
      "Sign four: you're embarrassed to share the link. If you catch yourself saying 'the website's a bit old, but...' that's your gut telling you it's time.",
      "Sign five: it doesn't actually do anything. A modern website should book appointments, take payments, or answer questions — not just sit there like a digital brochure. The good news: a refresh doesn't have to cost the earth. Our Starter package gets small businesses back online properly for $799, usually within a week.",
    ],
  },
  {
    slug: "ai-save-10-hours",
    title: "How AI Can Save You 10 Hours a Week (Without the Tech Headache)",
    excerpt: "AI isn't just for big companies with big budgets. Here are four boring, practical ways it can give you your evenings back.",
    date: "July 2026",
    readTime: "5 min read",
    tag: "AI for Business",
    body: [
      "Forget the sci-fi stuff. For a small business, AI is simply a way to stop doing the same repetitive tasks over and over — the admin that eats your evenings and weekends.",
      "First: answering the same questions. Opening hours, pricing, 'do you service my area?' An AI chatbot on your website handles these instantly, at 2pm or 2am, and only bothers you when a human is genuinely needed.",
      "Second: writing social media posts. Staring at a blank caption box at 9pm is nobody's idea of fun. AI tools can draft a month of posts in your tone of voice — a human just reviews and schedules them.",
      "Third: following up enquiries. Automated emails and booking reminders mean no more sticky notes and forgotten callbacks. Customers feel looked after, and nothing slips through the cracks.",
      "Fourth: quotes and invoices. Modern tools can draft quotes from a few details and chase overdue invoices politely but persistently — so you get paid faster without the awkward phone calls.",
      "The pattern here isn't replacing people. It's removing the repetitive layer so you can spend your time on the work that actually makes money. Most of these can be set up once and then largely run themselves.",
    ],
  },
  {
    slug: "professional-email-matters",
    title: "What a Professional Email Address Says About Your Business",
    excerpt: "Customers notice more than you think. Here's why you@yourbusiness.com.au beats yourbusiness88@gmail.com every time.",
    date: "June 2026",
    readTime: "3 min read",
    tag: "Branding",
    body: [
      "Imagine two quotes land in a customer's inbox. One comes from dave.plumbing.syd@gmail.com. The other from dave@coastalplumbing.com.au. Same price, same service — but most people instinctively trust the second one more.",
      "A professional email address signals that you're established, that you'll still be around next year, and that you take your business seriously. It's a tiny detail that does a lot of quiet heavy lifting.",
      "There's a practical side too. Business email comes with better spam protection, proper calendars and contacts that sync across your phone and computer, and the ability to add addresses like quotes@ or bookings@ as you grow.",
      "Setting it up involves registering a domain, configuring email hosting, and connecting your devices — about an hour of technical work that most people put off for years because it sounds complicated.",
      "It isn't, when someone does it for you. Domain and business email setup is included in every Avora package, and it's one of the fastest credibility upgrades a small business can make.",
    ],
  },
  {
    slug: "online-bookings-fill-calendar",
    title: "Online Bookings: The Easiest Way to Fill Your Calendar",
    excerpt: "Every missed call is a missed booking. Here's how letting customers book themselves changes the game.",
    date: "June 2026",
    readTime: "4 min read",
    tag: "Bookings",
    body: [
      "Here's an uncomfortable truth for service businesses: a big chunk of your potential customers try to book outside business hours. If booking means calling you between 9 and 5, many of them simply won't bother — they'll book with a competitor who lets them do it online.",
      "An online booking system flips this. A customer finds you on Google at 10pm, picks a time that suits them, and gets an instant confirmation. You wake up to a fuller calendar without lifting a finger.",
      "The benefits compound. Automatic reminders slash no-shows — one of the biggest hidden costs for salons, clinics, and tradies. Optional deposits mean last-minute cancellations stop hurting. And your calendar syncs, so double-bookings become a thing of the past.",
      "There's also the admin dividend. Business owners tell us they get back three to five hours a week previously spent on phone tag, text messages, and diary shuffling.",
      "Setup is simpler than most people expect. A booking calendar sits right on your website, matched to your branding, connected to your existing calendar. It's included in our Growth package, or available as an add-on to any site.",
    ],
  },
];

export const CONTACT = {
  email: "info@avorabusinesssolutions.com.au",
  phone: "+61 400 000 000",
  location: "Sydney, NSW, Australia",
  whatsapp: "https://wa.me/61400000000",
  instagram: "https://www.instagram.com/avorabusinesssolutions",
  facebook: "https://www.facebook.com/profile.php?id=61592895387330",
  linkedin: "https://linkedin.com/company/avora-solutions",
};
